import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { Icon } from '@nearpod-packages/base-library';
import styles from './Thumbnail.scss.js';
import ThreeDotsButton from './ThreeDotsButton/ThreeDotsButton.js';
import { getBannerInfo } from '../../../helpers/thumbnail/getThumbnailBannerInfo.js';
import { useCustomTranslation } from '../Hooks/useCustomTranslation/useCustomTranslation.js';
import Tooltip from '../Tooltip/Tooltip/Tooltip.js';
import useStaticHandler from '../Hooks/useStaticHandler/useStaticHandler.js';

const BOTTOM_SPACE_FROM_BUTTON = 4;
const Thumbnail = _ref => {
  let {
    id,
    title = '',
    shouldShowErrors = false,
    isActive,
    isInvalid,
    canSelect,
    isSelected,
    type,
    thumbnailURL,
    canShowBlockedBanner = false,
    canShowMenuOptions = false,
    isContextMenuOpen = false,
    isStacked = false,
    isMultiStacked = false,
    isCreatedWithAI = false,
    handleContextMenuOpen
  } = _ref;
  const {
    tCustom
  } = useCustomTranslation();
  const [thumbnailOne, setThumbnailOne] = useState(thumbnailURL);
  const [thumbnailTwo, setThumbnailTwo] = useState(thumbnailURL);
  const [showThumbnailOne, setShowThumbnailOne] = useState(true);
  const containerRef = useRef(null);
  const buttonMenuRef = useRef(null);
  const isThumbnailReady = thumbnailOne.length > 0 || thumbnailTwo.length > 0;
  // If the Slide was just created we are not showing the red border even if it's invalid
  const isInvalidAndShouldShowErrors = isInvalid && shouldShowErrors && !isSelected;
  const updateThumbnail = useStaticHandler(url => {
    if (url === thumbnailOne || url === thumbnailTwo) return;
    // We are using the "Double buffering technique" to prevent any white flicker that might occur when
    // an image is loaded in real-time as it's being downloaded by the browser.
    // Now we just render the thumbnail if it's completely loaded
    const img = new Image();
    img.src = url;
    img.onload = () => {
      // Set the new thumbnail image URL and switch the thumbnail to show
      if (showThumbnailOne) {
        setThumbnailTwo(img.src);
        setShowThumbnailOne(false);
      } else {
        setThumbnailOne(img.src);
        setShowThumbnailOne(true);
      }
    };
  });
  useEffect(() => {
    updateThumbnail(thumbnailURL);
  }, [thumbnailURL, updateThumbnail]);

  // Get Info about the blocked slide
  let showBlockedBanner = false;
  let bannerInfo = {
    textKey: '',
    image: ''
  };
  if (canShowBlockedBanner) {
    showBlockedBanner = true;
    bannerInfo = getBannerInfo(type);
  }
  return /*#__PURE__*/React.createElement("div", {
    key: `thumbnail-${id}`,
    tabIndex: 0,
    role: "button",
    className: classNames(styles.container, {
      [styles['container--active']]: isActive,
      [styles['container--selected']]: canSelect && isSelected,
      [styles['container--disabled']]: !canSelect && !isActive,
      [styles['container--invalid']]: isInvalidAndShouldShowErrors,
      [styles['container--stacked']]: isStacked || isMultiStacked,
      [styles['container--multiStacked']]: isMultiStacked
    }),
    onContextMenu: event => {
      if (handleContextMenuOpen) {
        event.preventDefault();
        handleContextMenuOpen(event.clientX, event.clientY);
      }
    },
    ref: containerRef
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.container__wrapper,
    style: thumbnailURL && !isThumbnailReady ? {
      backgroundImage: `url(${thumbnailOne})`
    } : {}
  }, isThumbnailReady && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("img", {
    className: classNames({
      [styles.container__wrapper__thumbnail]: true,
      [styles['container__wrapper__thumbnail--active']]: showThumbnailOne
    }),
    src: thumbnailOne,
    alt: "thumbnail"
  }), /*#__PURE__*/React.createElement("img", {
    className: classNames({
      [styles.container__wrapper__thumbnail]: true,
      [styles['container__wrapper__thumbnail--active']]: !showThumbnailOne
    }),
    src: thumbnailTwo,
    alt: "thumbnail"
  })), canShowMenuOptions && /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['container__wrapper__menu-button'], {
      [styles['container__wrapper__menu-button--show']]: isContextMenuOpen
    })
  }, /*#__PURE__*/React.createElement(ThreeDotsButton, {
    onClick: event => {
      event.stopPropagation();
      if (buttonMenuRef.current) {
        if (handleContextMenuOpen) {
          const rect = buttonMenuRef.current.getBoundingClientRect();
          handleContextMenuOpen(rect.left, rect.bottom + BOTTOM_SPACE_FROM_BUTTON);
        }
      }
    },
    ref: buttonMenuRef
  })), showBlockedBanner && bannerInfo && /*#__PURE__*/React.createElement("div", {
    style: {
      backgroundImage: `url(${bannerInfo.image})`
    },
    className: styles.container__wrapper__banner
  }, /*#__PURE__*/React.createElement("p", {
    className: styles.container__wrapper__banner__text
  }, tCustom(bannerInfo.textKey)))), title.length > 0 && /*#__PURE__*/React.createElement("h3", {
    className: classNames(styles.container__title, {
      [styles['container__title--selected']]: canSelect && isSelected || isActive
    })
  }, title), isCreatedWithAI && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: styles.container__icon
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "np_awesome",
    size: "18",
    color: isActive ? 'colorNeutral90' : 'colorNeutral70'
  })), /*#__PURE__*/React.createElement("div", {
    className: styles['container__tooltip-container']
  }, /*#__PURE__*/React.createElement(Tooltip, {
    arrow: "right-center"
  }, tCustom('THUMBNAIL_AI_GENERATED')))));
};
var Thumbnail$1 = /*#__PURE__*/React.memo(Thumbnail);

export { Thumbnail$1 as default };
//# sourceMappingURL=Thumbnail.js.map
